import useTranslation from 'next-translate/useTranslation'
import { useState, useEffect, FC } from 'react'
import { Translate } from 'next-translate'
import {
  baseValidator,
  emailValidator,
  numberGreaterThanZeroValidator
} from '@/core/validationUtils'
import { Box, Grid } from '@/components/atoms/Grid'
import styled from 'styled-components'
import { AccentButton } from '@/components/atoms/button'
import {
  getValidationResult,
  initData,
  isFormValid,
  onInputChange
} from '@/components/molecules/form/form'
import { Input, InputValue } from '@/components/molecules/form/form'
import FormElement from '@/components/molecules/form/formElement'
import FormInput from '@/components/molecules/form/formInput'
import { useUser } from '@/providers/userProvider'

const makeEmailField = (t: Translate): Input => ({
  title: t('CheckOrder.Form.email'),
  name: 'email',
  rules: [
    {
      isValid: baseValidator,
      errorMessage: t('CheckOrder.Form.emptyEmailErrorPlaceholder')
    },
    {
      isValid: emailValidator,
      errorMessage: t('CheckOrder.Form.notValidEmailErrorPlaceholder')
    }
  ]
})

const makeOrderNumberField = (t: Translate, initialValue = ''): Input => ({
  title: t('CheckOrder.Form.orderNumber'),
  name: 'orderNumber',
  initialValue,
  rules: [
    {
      isValid: baseValidator,
      errorMessage: t('CheckOrder.Form.emptyOrderNumberErrorPlaceholder')
    },
    {
      isValid: numberGreaterThanZeroValidator,
      errorMessage: t('CheckOrder.Form.notValidOrderNumberErrorPlaceholder')
    }
  ]
})

const makeFormFields = (
  t: Translate,
  isLoggedIn: boolean,
  initialOrderNumber = ''
): Input[] => {
  const fields = [makeOrderNumberField(t, initialOrderNumber)]
  !isLoggedIn && fields.push(makeEmailField(t))
  return fields
}

type CheckOrderForm = {
  onSubmit: (orderId: string, email: string) => void
  orderNumber?: string
  useCheckLogin?: boolean
} & Rest

const CheckOrderForm: FC<CheckOrderForm> = ({
  onSubmit,
  orderNumber = '',
  children,
  useCheckLogin = true,
  ...rest
}) => {
  const { t } = useTranslation('common')
  const [showValidation, setShowValidation] = useState<boolean>(false)
  const [data, setData] = useState<Record<string, InputValue>>({})
  const { isLoggedIn } = useUser()
  const formFields = makeFormFields(
    t,
    useCheckLogin ? isLoggedIn : false,
    orderNumber
  )

  useEffect(() => {
    initData(formFields, setData)
  }, [])

  const validate = (): boolean => {
    setShowValidation(true)
    return isFormValid(data)
  }

  const onFormSubmit = (e) => {
    e.preventDefault()
    validate() && onSubmit(data.orderNumber?.value, data.email?.value)
  }

  return (
    <Box>
      <Grid
        gridTemplateColumns="1fr"
        gridRowGap={'16px'}
        gridColumnGap={'16px'}
        {...rest}
      >
        {formFields.map(
          ({ name, title, placeholder, rules, type, ...rest }) => {
            const { isValid, errorMessage } = getValidationResult(
              data[name],
              rules
            )
            return (
              <Box key={name} {...rest}>
                <FormElement showValidation={showValidation} isValid={isValid}>
                  <FormInput
                    name={name}
                    value={data[name]?.value}
                    placeholder={title || placeholder}
                    onChange={(name, value) =>
                      onInputChange(name, value, rules, setData)
                    }
                    formInputType={type}
                    isError={showValidation && !isValid}
                    errorPlaceholder={errorMessage}
                    adjustPlaceholderPosition
                  />
                </FormElement>
              </Box>
            )
          }
        )}
      </Grid>
      {children}
      <Box textAlign="right">
        <SubmitButton
          title={t('CheckOrder.Form.submit')}
          onClick={onFormSubmit}
        />
      </Box>
    </Box>
  )
}

export default CheckOrderForm

const SubmitButton = styled(AccentButton)`
  text-transform: uppercase;
`
